import { useEffect, useRef, useState } from 'react';
import { expenseApi } from '../lib/api.js';

// Auto-fills an expense line's exchange rate (currency -> SAR) for the
// line's own date, so the rate reflects the day the money was spent.
//
// Behavior:
//  - Skips SAR (always 1) and skips when there's no date yet.
//  - Re-fetches whenever the currency OR the date changes (the whole point
//    is date-accuracy), then calls onRate(rate).
//  - On the FIRST run, if the line already carries a saved non-default rate
//    (editing an existing item), it leaves that rate alone so opening an
//    item doesn't clobber its locked-in value. A later currency/date change
//    still re-fetches.
//  - A manual edit to the rate isn't disturbed, because this only runs when
//    currency/date change — typing in the rate field changes neither.
//  - Never throws/blocks: on a miss it just reports `error` and the field
//    stays manual.
//
// onRate and hasExistingRate are read through refs so the effect depends
// only on the reactive [currency, date].
export function useFxRate({ currency, date, hasExistingRate = false, onRate }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const firstRun = useRef(true);
  const onRateRef = useRef(onRate);
  const hadRateRef = useRef(hasExistingRate);
  onRateRef.current = onRate;
  hadRateRef.current = hasExistingRate;

  useEffect(() => {
    const isFirst = firstRun.current;
    firstRun.current = false;

    if (!currency || currency === 'SAR') {
      setError(false);
      return undefined;
    }
    if (isFirst && hadRateRef.current) return undefined; // keep a saved rate on open
    // Note: we fetch as soon as a non-SAR currency is chosen, even before a
    // date is set — the backend returns today's rate when date is empty, and
    // this effect re-runs to fetch the exact as-of-date rate once the date is
    // filled in. (Waiting for the date left the rate stuck at 1, which read
    // as "auto-fill is broken" when you switched currency first.)

    let cancelled = false;
    setLoading(true);
    setError(false);
    expenseApi
      .getFxRate(currency, date)
      .then((res) => {
        if (cancelled) return;
        if (res && typeof res.rate === 'number' && res.rate > 0) {
          onRateRef.current(res.rate);
        } else {
          setError(true);
        }
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [currency, date]);

  return { loading, error };
}
